/**
 * `<ErrorDetails>` — the shared, copyable detail block rendered inside every
 * error `Alert` (WSRADM-0047). The surface keeps its curated headline sentence
 * (FEED-003); this adds, beneath it, the server's reason, a muted reference such
 * as "(500 INTERNAL_ERROR)", a collapsed **Details** disclosure (request, request
 * id, staging diagnostic) and a one-click **Copy details**.
 *
 * Never a stack trace or raw JSON. Reads only what `describeError` extracts from
 * the caught error.
 */
import { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { describeError, formatErrorReport } from '@/api/errorReport';
import { useCopy } from '@/hooks/useCopy';

export interface ErrorDetailsProps {
  /** The caught error — `ApiError`, `Error`, or anything thrown. */
  error: unknown;
  /** The surface's own sentence; the first line of the copied report. */
  headline: string;
  /**
   * What the failed call was doing. A `'read'` (a load) 403 says the role lacks
   * access; the default `'write'` (a save / rebuild) says it can't make the change.
   */
  intent?: 'read' | 'write';
}

const COPIED_MS = 3000;

const wrap = { overflowWrap: 'anywhere' } as const;
const mono = { fontFamily: 'monospace', overflowWrap: 'anywhere' } as const;

export function ErrorDetails({ error, headline, intent = 'write' }: ErrorDetailsProps) {
  const { t } = useCopy();
  const report = describeError(error);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [manual, setManual] = useState(false);
  // Bumped on every failed copy so each one re-selects the Details text.
  const [failedCopies, setFailedCopies] = useState(0);
  const detailsRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    [],
  );

  // After a failed clipboard write the block is opened; select its text so the
  // person can copy it with the keyboard.
  useEffect(() => {
    if (!manual || !open || !detailsRef.current) return;
    const selection = window.getSelection();
    if (!selection) return;
    const range = document.createRange();
    range.selectNodeContents(detailsRef.current);
    selection.removeAllRanges();
    selection.addRange(range);
  }, [manual, open, failedCopies]);

  const isApiError = report.status !== null || report.code !== null;
  const isNetwork = report.code === 'NETWORK_ERROR';
  let reason: string | null = null;
  if (report.status === 403) {
    reason =
      intent === 'read'
        ? t('errors.forbiddenRead', 'Your role doesn’t have access to this. Ask an Owner or Admin.')
        : t('errors.forbidden', 'Your role can’t make this change. An Owner or Admin can.');
  } else if (isNetwork) {
    reason = t(
      'errors.network',
      'No response from the server — check the connection and try again.',
    );
  } else if (isApiError) {
    reason = report.message;
  }

  let reference: string | null = null;
  if (isNetwork) {
    reference = t('errors.networkRef', '(network error)');
  } else if (report.status !== null || report.code !== null) {
    reference = `(${[report.status, report.code].filter((v) => v !== null).join(' ')})`;
  }

  const diag = report.diagnostic;
  const request = [report.method, report.path].filter((v) => v !== null).join(' ');
  const rows: Array<{ label: string; value: string; mono: boolean }> = [];
  const label = (key: string, fallback: string): string => t(`errors.field.${key}`, fallback);
  if (request) rows.push({ label: label('request', 'Request'), value: request, mono: true });
  if (report.requestId !== null) {
    rows.push({ label: label('requestId', 'Request ID'), value: report.requestId, mono: true });
  }
  // A non-API error's message is not a "server reason" — it lives in Details.
  if (!isApiError && report.message !== null) {
    rows.push({ label: label('message', 'Message'), value: report.message, mono: false });
  }
  if (diag?.exception !== undefined) {
    rows.push({ label: label('exception', 'Exception'), value: diag.exception, mono: true });
  }
  if (diag?.message !== undefined) {
    rows.push({ label: label('message', 'Message'), value: diag.message, mono: false });
  }
  if (diag?.at !== undefined) {
    rows.push({ label: label('at', 'At'), value: diag.at, mono: true });
  }

  const handleCopy = async () => {
    const text = formatErrorReport(report, {
      headline,
      pageUrl: window.location.href,
      at: new Date(),
      labels: {
        time: label('time', 'Time'),
        page: label('page', 'Page'),
        request: label('request', 'Request'),
        status: label('status', 'Status'),
        code: label('code', 'Code'),
        message: label('message', 'Message'),
        requestId: label('requestId', 'Request ID'),
        exception: label('exception', 'Exception'),
        at: label('at', 'At'),
      },
    });
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(text);
      setManual(false);
      setCopied(true);
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), COPIED_MS);
    } catch {
      setCopied(false);
      setOpen(true);
      setManual(true);
      setFailedCopies((n) => n + 1);
    }
  };

  return (
    <Box sx={{ mt: 0.5, ...wrap }}>
      {reason ? (
        <Typography variant="body2" sx={wrap}>
          {reason}
        </Typography>
      ) : null}
      {reference ? (
        <Typography variant="caption" color="text.secondary" component="div" sx={wrap}>
          {reference}
        </Typography>
      ) : null}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
        <Button
          size="small"
          color="inherit"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {t('errors.details', 'Details')}
        </Button>
        <Button size="small" color="inherit" onClick={() => void handleCopy()}>
          {t('errors.copy', 'Copy details')}
        </Button>
        <Typography variant="caption" component="span" role="status" sx={wrap}>
          {copied ? t('errors.copied', 'Copied') : ''}
        </Typography>
      </Box>
      {open ? (
        <Box ref={detailsRef} sx={{ mt: 0.5, color: 'text.secondary', ...wrap }}>
          {rows.length === 0 ? (
            <Typography variant="caption" component="div" sx={wrap}>
              {headline}
            </Typography>
          ) : null}
          {rows.map((row, i) => (
            <Typography key={`${row.label}-${i}`} variant="caption" component="div" sx={wrap}>
              {row.label}:{' '}
              <Box component="span" sx={row.mono ? mono : wrap}>
                {row.value}
              </Box>
            </Typography>
          ))}
        </Box>
      ) : null}
      {manual ? (
        <Typography variant="caption" component="div" sx={wrap}>
          {t('errors.copyManual', 'Select and copy the details above.')}
        </Typography>
      ) : null}
    </Box>
  );
}

export default ErrorDetails;
